// @flow

import * as React from 'react';
import { compose, graphql } from 'react-apollo';
import { connect } from 'react-redux';
import { localize } from '@ta/common.i18n';

import { Icon } from '@ta/styleguide.icon';
import type { SocialObjectReference } from '@ta/social.types';
import { FeedObjectStatisticFieldsFragment } from '@ta/social.feed';
import classNames from '@ta/common.classnames';

import Boost from './AddBoost.gql';
import Unboost from './DeleteBoost.gql';
import socialStyles from './SocialAction.less';

type ExternalProps = {|
  socialObjectRef: SocialObjectReference,
  socialStatistics: any,
  typename: string,
|};

type MutationProps = {
  __typename: string,
  socialObjectRef: SocialObjectReference,
  socialStatistics: any,
};

type Props = {|
  loggedInUserId: ?string,
  +boost: MutationProps => Promise<*>,
  /** provided by graphql */
  +unboost: MutationProps => Promise<*>,
  ...ExternalProps,
|};

class BoostActionPresentation extends React.Component<Props> {
  memberGeneratedAction = () => {
    if (this.isActive()) {
      return this.props.unboost({
        socialObjectRef: this.props.socialObjectRef,
        __typename: this.props.typename,
        socialStatistics: this.props.socialStatistics,
      });
    }

    return this.props.boost({
      socialObjectRef: this.props.socialObjectRef,
      __typename: this.props.typename,
      socialStatistics: this.props.socialStatistics,
    });
  };

  isActive = () => this.props.socialStatistics && this.props.socialStatistics.isBoosted;

  render() {
    return (
      <span
        className={classNames(socialStyles.social_action, {
          [socialStyles.active]: this.isActive(),
        })}
        onClick={this.memberGeneratedAction}
      >
        <Icon name="instant" className={socialStyles.icon} />
        <span className={`ui_link ${socialStyles.text} socialCTA`}>
          {this.isActive() ? localize('boosted_social') : localize('boost_social')}
        </span>
      </span>
    );
  }
}

const BoostAction = compose(
  // $FlowFixGraphql
  connect(state => ({
    loggedInUserId: state.auth.loggedInUserId,
  })),

  // $FlowFixGraphql
  graphql(Boost, {
    props: ({ mutate }) => ({
      boost: ({ socialObjectRef, socialStatistics, __typename }: MutationProps) =>
        mutate({
          variables: { objectType: socialObjectRef.type, objectId: socialObjectRef.id },
          update: cache => {
            // Increment the boost count
            const { boostCount = 0 } = socialStatistics || {};
            /* eslint-disable no-nested-ternary */
            const keyName =
              socialObjectRef.type === 'LinkPost'
                ? 'postId'
                : socialObjectRef.type === 'Repost'
                ? 'repostId'
                : 'id';
            cache.writeFragment({
              id: `${__typename}:${socialObjectRef.id}`,
              fragment: FeedObjectStatisticFieldsFragment,
              fragmentName: 'FeedObjectStatisticFields',
              data: {
                __typename,
                [keyName]: socialObjectRef.id,
                socialStatistics: {
                  ...socialStatistics,
                  isBoosted: true,
                  boostCount: boostCount + 1,
                  __typename: 'SocialStatistics',
                },
              },
            });
          },
        }),
    }),
  }),

  // $FlowFixGraphql
  graphql(Unboost, {
    props: ({ mutate }) => ({
      unboost: ({ socialObjectRef, socialStatistics, __typename }: MutationProps) =>
        mutate({
          variables: { objectType: socialObjectRef.type, objectId: socialObjectRef.id },
          update: cache => {
            // Decrement the boost count
            const { boostCount = 1 } = socialStatistics || {};
            /* eslint-disable no-nested-ternary */
            const keyName =
              socialObjectRef.type === 'LinkPost'
                ? 'postId'
                : socialObjectRef.type === 'Repost'
                ? 'repostId'
                : 'id';
            cache.writeFragment({
              id: `${__typename}:${socialObjectRef.id}`,
              fragment: FeedObjectStatisticFieldsFragment,
              fragmentName: 'FeedObjectStatisticFields',
              data: {
                __typename,
                [keyName]: socialObjectRef.id,
                socialStatistics: {
                  ...socialStatistics,
                  isBoosted: false,
                  boostCount: boostCount - 1,
                  __typename: 'SocialStatistics',
                },
              },
            });
          },
        }),
    }),
  }),
)(BoostActionPresentation);

export default BoostAction;
